import { useState, useEffect } from "react";
import { z } from "zod";
import { PopoverClose } from "@radix-ui/react-popover";
import { PlusIcon, TrashIcon, InfoIcon } from "lucide-react";
import { Input } from "../ui/input";
import { Button } from "../ui/button";
import { attributesValidator } from "../../models/schemasV2";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../ui/accordion";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { HandleAttributeValueGroupActionParams } from "../../hooks/templateV2/types";
import { Textarea } from "../ui/textarea";
import { Switch } from "../ui/switch";
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from "../ui/hover-card";

interface SingleValuePopoverProps {
  onSave: (data: HandleAttributeValueGroupActionParams) => void;
  valueGroupId?: string;

  attribute?: z.infer<typeof attributesValidator>;
}

const SingleValuePopover = ({
  onSave,
  valueGroupId,
  attribute,
}: SingleValuePopoverProps) => {
  const [name, setName] = useState<string>(attribute?.name || "");
  const [description, setDescription] = useState<string>(
    attribute?.description || "",
  );
  const [type, setType] = useState<string>(attribute?.type || "string");
  const [unit, setUnit] = useState<string | undefined>(
    attribute?.unit || undefined,
  );
  const [presets, setPresets] = useState<string[]>(attribute?.enum || []);
  const [required, setRequired] = useState<boolean>(
    attribute?.required || false,
  );
  const [inferred, setInferred] = useState<boolean>(
    attribute?.inferred || false,
  );
  useEffect(() => {
    if (attribute) {
      setName(attribute.name);
      setDescription(attribute.description);
      setPresets(attribute.enum || []);
      setInferred(attribute.inferred);
    }
  }, [attribute]);

  const isEditMode = !!attribute;

  const handleSave = () => {
    onSave({
      valueGroupId: valueGroupId || "",
      attributeName: name,
      attributeDescription: description,
      attributeType: type,
      attributeId: attribute?.id,
      required,
      unit,
      presets,
      inferred,
      deleteAttribute: false,
    });
  };

  const handleDelete = () => {
    if (isEditMode) {
      onSave({
        valueGroupId: valueGroupId || "",
        attributeId: attribute?.id,
        attributeName: name,
        attributeDescription: description,
        attributeType: type,
        unit,
        presets,
        required,
        inferred,
        deleteAttribute: true,
      });
    }
  };

  const handleAddPreset = () => {
    if (presets) {
      setPresets([...presets, ""]);
    } else {
      setPresets([""]);
    }
  };

  const handlePresetChange = (index: number, value: string) => {
    if (presets) {
      const newPresets = [...presets];
      newPresets[index] = value;
      setPresets(newPresets);
    }
  };

  const handleDeletePreset = (index: number) => {
    if (presets) {
      const newPresets = presets.filter((_, i) => i !== index);
      setPresets(newPresets);
    }
  };

  return (
    <div className="flex flex-col gap-2 overflow-y-auto max-h-[calc(var(--radix-popover-content-available-height)-100px)] px-1">
      {isEditMode && (
        <PopoverClose asChild>
          <div className="flex justify-end">
            <TrashIcon
              className="cursor-pointer"
              onClick={handleDelete}
              size={16}
            />
          </div>
        </PopoverClose>
      )}
      <div className="flex flex-col gap-1">
        <label htmlFor="attributeName" className="text-sm">
          Attribute Name
        </label>
        <Input
          placeholder="Attribute Name"
          type="text"
          id="attributeName"
          onChange={(e) => setName(e.target.value)}
          value={name}
        />
      </div>
      <div className="flex flex-col gap-1">
        <label htmlFor="aiInstructions" className="text-sm">
          AI Instructions (Optional)
        </label>
        <Textarea
          placeholder="AI Instructions"
          id="aiInstructions"
          onChange={(e) => setDescription(e.target.value)}
          value={description}
        />
      </div>

      <Accordion type="single" collapsible>
        <AccordionItem className="border-0" value="item-1">
          <AccordionTrigger className="text-sm font-normal">
            Advanced Settings
          </AccordionTrigger>
          <AccordionContent className="flex flex-col gap-4">
            <div className="flex gap-4">
              <div className="flex items-center space-x-2">
                <Switch
                  id="required"
                  checked={required}
                  onCheckedChange={(checked) => setRequired(checked)}
                />
                <label htmlFor="required">Required</label>
              </div>
              <div className="flex items-center space-x-2">
                <Switch
                  id="inferred"
                  checked={inferred}
                  onCheckedChange={(checked) => setInferred(checked)}
                />
                <label htmlFor="inferred" className="flex items-center gap-1">
                  Inferred from AI (Beta)
                  <HoverCard openDelay={100}>
                    <HoverCardTrigger>
                      <InfoIcon
                        size={14}
                        className="text-muted-foreground cursor-pointer"
                      />
                    </HoverCardTrigger>
                    <HoverCardContent className="w-80 p-4">
                      <p className="text-sm">
                        When enabled, this attribute's value will be
                        automatically inferred by AI instead of pulled from the
                        uploaded document. For example: If you want a summary of
                        this document, you can enable this and name the
                        attribute "summary". This is a beta feature.
                      </p>
                    </HoverCardContent>
                  </HoverCard>
                </label>
              </div>
            </div>
            <div className="flex gap-2 p-1 mb-8">
              <div className="flex flex-col gap-1 w-1/2">
                <label className="text-sm">Data Type</label>
                <Select
                  value={type}
                  onValueChange={(value) => {
                    if (value !== "number") {
                      setUnit(undefined);
                    }
                    if (value !== "string") {
                      setPresets([]);
                    }
                    setType(value);
                  }}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select a data type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="string">Text</SelectItem>
                    <SelectItem value="number">Number</SelectItem>
                    <SelectItem value="boolean">True/False</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {/*{type === "number" && (*/}
              {/*  <div className="flex flex-col gap-1 w-1/2">*/}
              {/*    <label className="text-sm">Base Unit</label>*/}
              {/*    <Select*/}
              {/*      value={unit}*/}
              {/*      onValueChange={(value) =>*/}
              {/*        setUnit(value === "none" ? undefined : value)*/}
              {/*      }*/}
              {/*    >*/}
              {/*      <SelectTrigger>*/}
              {/*        <SelectValue placeholder="Select a base unit" />*/}
              {/*      </SelectTrigger>*/}
              {/*      <SelectContent>*/}
              {/*        <SelectItem value="none">None</SelectItem>*/}
              {/*        <SelectItem value="day">Daily</SelectItem>*/}
              {/*        <SelectItem value="week">Weekly</SelectItem>*/}
              {/*        <SelectItem value="month">Monthly</SelectItem>*/}
              {/*        <SelectItem value="year">Year</SelectItem>*/}
              {/*      </SelectContent>*/}
              {/*    </Select>*/}
              {/*  </div>*/}
              {/*)}*/}
              {type === "string" && (
                <div className="flex flex-col gap-1 w-1/2">
                  <label className="text-sm">Presets</label>
                  <div className="flex flex-col gap-2">
                    {presets.map((preset, index) => (
                      <div key={index} className="flex items-center gap-2">
                        <Input
                          type="text"
                          value={preset}
                          onChange={(e) =>
                            handlePresetChange(index, e.target.value)
                          }
                        />
                        <TrashIcon
                          size={16}
                          className="cursor-pointer"
                          onClick={() => handleDeletePreset(index)}
                        />
                      </div>
                    ))}
                    <Button
                      onClick={handleAddPreset}
                      className="flex items-center gap-1"
                    >
                      <PlusIcon size={16} />
                      Add Preset
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </AccordionContent>
        </AccordionItem>
      </Accordion>
      <div className="sticky bottom-0 bg-white p-2">
        <PopoverClose asChild>
          <Button className="w-full" onClick={handleSave}>
            Save
          </Button>
        </PopoverClose>
      </div>
    </div>
  );
};

export default SingleValuePopover;
